import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { Block, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { useError } from '../../../hooks/useError'
import usePage from '../../../hooks/usePage'
import languageService from '../../../services/LanguageService'
import PageService from '../../../services/PageService'
import { BackButton } from '../../components/BackButton/BackButton'
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar'
import {
  BlueprintFamiliesEventFilterBar,
  UNCATEGORIZED_FILTER_ID,
} from '../../features/live-events-tracking/components/BlueprintFamiliesEventFilterBar/BlueprintFamiliesEventFilterBar'
import { BlueprintFamiliesOverviewTable } from '../../features/live-events-tracking/components/BlueprintFamiliesOverviewTable/BlueprintFamiliesOverviewTable'
import { BlueprintFamilyDataIssuesCard } from '../../features/live-events-tracking/components/BlueprintFamilyDataIssuesCard/BlueprintFamilyDataIssuesCard'
import { BlueprintFamilyGamesTable } from '../../features/live-events-tracking/components/BlueprintFamilyGamesTable/BlueprintFamilyGamesTable'
import { BlueprintFamilyOddCasesCard } from '../../features/live-events-tracking/components/BlueprintFamilyOddCasesCard/BlueprintFamilyOddCasesCard'
import { BlueprintFamilyOddTypesCard } from '../../features/live-events-tracking/components/BlueprintFamilyOddTypesCard/BlueprintFamilyOddTypesCard'
import { useBlueprintFamilyEventTypes } from '../../features/live-events-tracking/hooks/useBlueprintFamilyEventTypes'
import { BlueprintFamilyUsage, useBlueprintFamilyUsage } from '../../features/live-events-tracking/hooks/useBlueprintFamilyUsage'

type BlueprintFamiliesOverviewPageProps = {
  children?: ReactNode
}

/**
 * Internal data sanity check page for Blueprint Families.
 *
 * Without a blueprint id in the URL the page lists all Blueprint Families
 * with usage counts and data quality issues. With a blueprint id it shows the
 * games (and their game-level blueprints) tagged to the selected blueprint.
 */
export const BlueprintFamiliesOverviewPage: FC<BlueprintFamiliesOverviewPageProps> = ({ children }) => {
  usePage(PageService.getInternalPageWithId('internal-blueprint-families-overview'))
  const { blueprintId } = useParams<{ blueprintId?: string }>()
  const { usages, usageById, dataIssues, isLoading, error } = useBlueprintFamilyUsage()
  useError({ error })

  const selectedUsage = blueprintId ? usageById[blueprintId] : undefined

  return (
    <>
      {!blueprintId && <BlueprintsListView usages={usages} dataIssues={dataIssues} isLoading={isLoading} />}
      {blueprintId && <BlueprintDetailView usage={selectedUsage} isLoading={isLoading} />}

      <FloatingToolbar>
        <BackButton mb={0} url={blueprintId ? '/internal/live-events-tracking/blueprint-families/overview' : '/internal/live-events-tracking'} viewTransition />
      </FloatingToolbar>
    </>
  )
}

type BlueprintCategoryGroup = {
  id: string
  name: string
  usages: BlueprintFamilyUsage[]
}

const BlueprintsListView: FC<{
  usages: BlueprintFamilyUsage[]
  dataIssues: ReturnType<typeof useBlueprintFamilyUsage>['dataIssues']
  isLoading: boolean
}> = ({ usages, dataIssues, isLoading }) => {
  const { t } = useTranslation()
  const { eventCategories } = useBlueprintFamilyEventTypes()
  const [searchText, setSearchText] = useState('')
  const [showDiscontinued, setShowDiscontinued] = useState(true)
  const [selectedEventCategoryIds, setSelectedEventCategoryIds] = useState<string[]>([])
  const [selectedEventTypeIds, setSelectedEventTypeIds] = useState<string[]>([])

  const toggleEventCategory = useCallback((categoryId: string) => {
    setSelectedEventCategoryIds((previous) => (previous.includes(categoryId) ? previous.filter((id) => id !== categoryId) : [...previous, categoryId]))
    // Changing the category scope invalidates event-type selections that may no
    // longer belong to the visible categories (mirrors Market Analytics).
    setSelectedEventTypeIds([])
  }, [])

  const toggleEventType = useCallback((typeId: string) => {
    setSelectedEventTypeIds((previous) => (previous.includes(typeId) ? previous.filter((id) => id !== typeId) : [...previous, typeId]))
  }, [])

  const clearEventCategories = useCallback(() => {
    setSelectedEventCategoryIds([])
    setSelectedEventTypeIds([])
  }, [])

  const clearEventTypes = useCallback(() => setSelectedEventTypeIds([]), [])

  // Base rows for the chip counts: only the discontinued toggle applies, so
  // counts stay stable while the user searches or toggles chips.
  const countBaseUsages = useMemo(() => usages.filter((usage) => showDiscontinued || !usage.blueprintFamily.discontinued), [usages, showDiscontinued])

  const filteredUsages = useMemo(() => {
    const search = searchText.trim().toLowerCase()
    const categorySet = selectedEventCategoryIds.length > 0 ? new Set(selectedEventCategoryIds) : null
    const typeSet = selectedEventTypeIds.length > 0 ? new Set(selectedEventTypeIds) : null
    return countBaseUsages
      .filter((usage) => !search || (usage.blueprintFamily.names.en || '').toLowerCase().includes(search))
      .filter((usage) => !categorySet || categorySet.has(usage.eventCategoryId || UNCATEGORIZED_FILTER_ID))
      .filter((usage) => !typeSet || (usage.eventTypeId ? typeSet.has(usage.eventTypeId) : false))
  }, [countBaseUsages, searchText, selectedEventCategoryIds, selectedEventTypeIds])

  // Groups the filtered rows by customer-facing event category. Categories follow the tag
  // group `order`, unknown category ids (not in the event tag groups) come after them, and
  // uncategorized blueprints always land in the last group. Empty groups are hidden.
  const categoryGroups = useMemo(() => {
    const usagesByCategoryId: { [categoryId: string]: BlueprintFamilyUsage[] } = {}
    filteredUsages.forEach((usage) => {
      const categoryId = usage.eventCategoryId || UNCATEGORIZED_FILTER_ID
      usagesByCategoryId[categoryId] = usagesByCategoryId[categoryId] || []
      usagesByCategoryId[categoryId].push(usage)
    })

    const knownCategoryIds = new Set(eventCategories.map((category) => category.id))
    const groups: BlueprintCategoryGroup[] = eventCategories
      .filter((category) => usagesByCategoryId[category.id]?.length)
      .map((category) => ({ id: category.id, name: category.name, usages: usagesByCategoryId[category.id] }))

    Object.keys(usagesByCategoryId)
      .filter((categoryId) => categoryId !== UNCATEGORIZED_FILTER_ID && !knownCategoryIds.has(categoryId))
      .forEach((categoryId) => {
        groups.push({
          id: categoryId,
          name: languageService.getTranslation('tagGroups', categoryId) || categoryId,
          usages: usagesByCategoryId[categoryId],
        })
      })

    if (usagesByCategoryId[UNCATEGORIZED_FILTER_ID]?.length) {
      groups.push({
        id: UNCATEGORIZED_FILTER_ID,
        name: t('internal-live-events:uncategorized'),
        usages: usagesByCategoryId[UNCATEGORIZED_FILTER_ID],
      })
    }

    return groups
  }, [filteredUsages, eventCategories, t])

  return (
    <>
      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:blueprint_families_overview')} />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <TextField
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                variant="outlined"
                size="small"
                label={t('internal-live-events:search_blueprint_families')}
                fullWidth
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Switch checked={showDiscontinued} onChange={(event) => setShowDiscontinued(event.target.checked)} />}
                label={t('internal-live-events:show_discontinued_blueprints')}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
            <BlueprintFamiliesEventFilterBar
              usages={countBaseUsages}
              eventCategories={eventCategories}
              selectedEventCategoryIds={selectedEventCategoryIds}
              selectedEventTypeIds={selectedEventTypeIds}
              onToggleEventCategory={toggleEventCategory}
              onToggleEventType={toggleEventType}
              onClearEventCategories={clearEventCategories}
              onClearEventTypes={clearEventTypes}
              onClearAll={clearEventCategories}
              isLoading={isLoading}
            />
          </Box>
        </CardContent>
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          {isLoading || categoryGroups.length === 0 ? (
            <BlueprintFamiliesOverviewTable usages={[]} isLoading={isLoading} />
          ) : (
            categoryGroups.map((group, groupIndex) => (
              <Box key={group.id}>
                {groupIndex > 0 && <Divider />}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="h3">{group.name}</Typography>
                  <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:blueprint_families_count', { count: group.usages.length })} />
                </Stack>
                <Divider />
                <BlueprintFamiliesOverviewTable usages={group.usages} />
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {!isLoading && <BlueprintFamilyOddTypesCard blueprintFamilies={usages.map((usage) => usage.blueprintFamily)} />}

      {!isLoading && <BlueprintFamilyDataIssuesCard dataIssues={dataIssues} />}
    </>
  )
}

const BlueprintDetailView: FC<{ usage?: BlueprintFamilyUsage; isLoading: boolean }> = ({ usage, isLoading }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <Card sx={{ marginTop: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={300} height={40} />
          <Skeleton variant="rounded" height={120} sx={{ marginTop: 2 }} />
        </CardContent>
      </Card>
    )
  }

  if (!usage) {
    return (
      <Card sx={{ marginTop: 3 }}>
        <CardContent>{t('internal-live-events:no_blueprint_families_found')}</CardContent>
      </Card>
    )
  }

  const descriptionHtml = usage.blueprintFamily.descriptions?.en || ''

  return (
    <>
      <Card sx={{ marginTop: 3 }}>
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{usage.blueprintFamily.names.en}</span>
              {usage.blueprintFamily.discontinued && (
                <Chip size="small" color="error" icon={<Block />} sx={{ margin: 0 }} label={t('live-events:live_event_evolution_type_discontinued')} />
              )}
            </Stack>
          }
          action={
            <Button
              component={Link}
              to={`/internal/live-events-tracking/blueprint-families/edit/${usage.blueprintFamily.id}`}
              startIcon={<Edit />}
              variant="outlined"
            >
              {t('internal-live-events:edit_blueprint_family')}
            </Button>
          }
        />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ marginBottom: descriptionHtml ? 2 : 0 }}>
            <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:linked_games_count', { count: usage.games.length })} />
            <Chip size="small" sx={{ margin: 0 }} label={t('internal-live-events:linked_game_blueprints_count', { count: usage.gameBlueprintCount })} />
          </Stack>
          {descriptionHtml && <Box dangerouslySetInnerHTML={{ __html: descriptionHtml }} />}
        </CardContent>
      </Card>

      <BlueprintFamilyOddCasesCard usage={usage} />

      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:games_using_blueprint_family')} />
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <BlueprintFamilyGamesTable usage={usage} />
        </CardContent>
      </Card>
    </>
  )
}
